<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('reset-password') }}</h4>

    <div class="d-flex card-body justify-content-center">
      <div class="reset-form">
        <!-- Toggle Email/SMS — pokazywany tylko jeśli SMS-reset jest włączony przez feature flag.
             Gdy SMS off, widok zachowuje się dokładnie jak wcześniej (czyste pole email). -->
        <div
          v-if="smsResetEnabled && !emailSent"
          class="channel-tabs btn-group w-100"
          role="tablist"
        >
          <button
            type="button"
            class="btn"
            :class="channel === 'email' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="setChannel('email')"
          >
            {{ t('reset-via-email') }}
          </button>
          <button
            type="button"
            class="btn"
            :class="channel === 'sms' ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="setChannel('sms')"
          >
            {{ t('reset-via-sms') }}
          </button>
        </div>

        <!-- Wariant EMAIL (domyślny / istniejący) -->
        <Form
          v-if="channel === 'email' && !emailSent"
          @submit="onSubmitEmail"
          :validation-schema="emailSchema"
          v-slot="{ errors, isSubmitting }"
        >
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('email') }}</label>
            <div class="col-sm-10">
              <Field
                name="email"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
              />
              <div class="invalid-feedback">{{ errors.email }}</div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto hint">
              {{ t('type-your-email-send-reset-token') }}
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary mt-3" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('send-email') }}
              </button>
            </div>
          </div>
        </Form>

        <!-- Wariant SMS — prefix kierunkowy (3 znaki, np. "+48") + 9 cyfr -->
        <Form
          v-else-if="channel === 'sms'"
          @submit="onSubmitSms"
          :validation-schema="smsSchema"
          :initial-values="{ phoneNumberDir: '+48', phoneNumber: '' }"
          v-slot="{ errors, isSubmitting }"
        >
          <div class="form-group row mb-1 phone-row">
            <label class="col-sm-2 col-form-label">{{ t('phone-number') }}</label>
            <div class="col-sm-3 pe-0">
              <Field
                name="phoneNumberDir"
                type="text"
                maxlength="3"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumberDir }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumberDir }}</div>
            </div>
            <div class="col-sm-7 ps-1">
              <Field
                name="phoneNumber"
                type="text"
                inputmode="numeric"
                maxlength="9"
                class="form-control"
                :class="{ 'is-invalid': errors.phoneNumber }"
              />
              <div class="invalid-feedback">{{ errors.phoneNumber }}</div>
            </div>
          </div>

          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto hint">
              {{ t('type-your-phone-send-sms-code') }}
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary mt-3" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ t('send-sms-code') }}
              </button>
            </div>
          </div>
        </Form>

        <!-- Potwierdzenie wysłania linku. Formularz znika, bo operacja jest
             zakończona — zostaje komunikat, wyraźny przycisk powrotu do logowania
             i odliczanie automatycznego przekierowania. -->
        <div v-if="emailSent" class="reset-sent" role="status" aria-live="polite">
          <p class="reset-sent__text">{{ t('reset-email-sent-info') }}</p>
          <button type="button" class="ds-btn ds-btn--primary" @click="goToLogin">
            {{ t('go-to-login') }}
          </button>
          <p class="reset-sent__countdown">
            {{ t('redirect-to-login-in', { seconds: secondsLeft }) }}
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useNotificationStore } from '@/stores/notification.store';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();

// Feature flag z env. Frontend i backend muszą być uzgodnione — gdy backend ma SMS off,
// front też powinien (odpytanie endpointu SMS i tak zwróci 204, ale UI jest mylące).
const smsResetEnabled = String(process.env.VUE_APP_FEATURE_SMS_RESET_ENABLED).toLowerCase() === 'true';

const notify = useNotificationStore();

const channel = ref('email');

// Po udanej wysyłce linku chowamy formularz i pokazujemy potwierdzenie z przyciskiem
// „Przejdź do logowania". Dodatkowo odliczamy automatyczne przekierowanie — użytkownik
// i tak musi teraz odebrać pocztę, więc nie ma po co trzymać go na tym ekranie.
const REDIRECT_SECONDS = 10;
const emailSent = ref(false);
const secondsLeft = ref(REDIRECT_SECONDS);
let countdownTimer = null;

const emailSchema = Yup.object({
  email: Yup.string()
    .email(t('incorrect-email-address'))
    .required(t('user-email-required')),
});

const smsSchema = Yup.object({
  phoneNumberDir: Yup.string()
    .matches(/^\+\d{2}$/, t('phone-prefix-invalid'))
    .required(t('phone-prefix-required')),
  phoneNumber: Yup.string()
    .matches(/^\d{9}$/, t('phone-number-invalid'))
    .required(t('phone-number-required')),
});

function setChannel(c) {
  channel.value = c;
}

function stopCountdown() {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function goToLogin() {
  stopCountdown();
  router.push('/login');
}

function startRedirectCountdown() {
  stopCountdown();
  secondsLeft.value = REDIRECT_SECONDS;
  countdownTimer = setInterval(() => {
    secondsLeft.value -= 1;
    if (secondsLeft.value <= 0) goToLogin();
  }, 1000);
}

// Timer musi zniknąć razem z widokiem — inaczej po ręcznym przejściu gdzie indziej
// wyrzuciłby użytkownika na /login w trakcie innej czynności.
onBeforeUnmount(stopCountdown);

async function onSubmitEmail(values) {
  try {
    await authStore.resetPassword(values.email);
    notify.success(t('reset-email-sent-info'));
    emailSent.value = true;
    startRedirectCountdown();
  } catch (error) {
    notify.error(error || t('reset-email-send-failed'));
  }
}

async function onSubmitSms(values) {
  try {
    await authStore.requestSmsResetCode(values.phoneNumberDir, values.phoneNumber);
    // BE zawsze 204; jeśli numer nie istnieje, w UI nie zdradzamy tego — przechodzimy
    // do widoku weryfikacji tak czy inaczej. Gdyby numer nie był zarejestrowany, user
    // po prostu nie dostanie SMS-a i kod nigdy nie zadziała.
    router.push('/reset-password/code');
  } catch (error) {
    notify.error(error || t('sms-code-send-failed'));
  }
}
</script>

<style scoped>
.card-body {
  background: var(--surface-secondary);
}
.reset-form {
  width: 460px;
  max-width: 100%;
}
.channel-tabs {
  margin-bottom: 1.25rem;
}
.channel-tabs .btn {
  border-radius: var(--control-radius);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  padding: 6px 12px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: var(--font-size-sm);
}
.hint {
  margin-top: 0.5rem;
}
.phone-row .form-control {
  padding: 10px;
}
/* Przyciski formularza (wysyłka e-maila / SMS-a). `.ds-btn` wyłączamy — przycisk
   powrotu do logowania ma zachować metrykę i kolory systemu przycisków. */
button:not(.btn-close):not(.ds-btn) {
  border: 0;
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}
.btn-outline-secondary {
  border: 1px solid grey;
}
input.form-control {
  border: 0;
  border-left: 1px solid var(--border-default);
  border-top: 1px solid var(--border-default);
  padding: 10px;
}
input.form-control:focus {
  border-color: var(--border-default);
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.reset-sent {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-lg) 0;
}
.reset-sent__text {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--text-primary);
}
.reset-sent__countdown {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}
</style>
